import { createSelector } from 'reselect';
import moment from 'moment';
import {
    getDiffInMinutes,
    humanizeMinutesDuration,
    formatTime,
    formatDate,
} from 'web-common/scripts/utils/DateHelper';

import {
    settingsSelector,
    markerSelector,
    markersSelector,
    shouldFetchAgendaSettingsSelector,
    shouldFetchMarkersSelector
} from './AgendaSelectors';

const getWebsites = speaker => {
    const websites = [];

    if (speaker.web) {
        websites.push({
            type: 'Website',
            link: speaker.web
        });
    }
    if (speaker.blog) {
        websites.push({
            type: 'Blog',
            link: speaker.blog
        });
    }
    return websites;
};

const getTwitter = speaker => {
    const twitter = speaker.twitterHandle ?
        `https://twitter.com/${speaker.twitterHandle}` :
        '';
    return twitter;
};

const getMarkerProps = (eventId, user, speaker) => ({
    eventId,
    userProfileId: user.id,
    entity: 'speakers',
    markerType: 'like',
    entityId: speaker.id
});

const getBio = (speaker, isLoggedIn, isLiked, speakerLikesCount) => ({
    name: `${speaker.firstname ? speaker.firstname + ' ' : ''}${speaker.lastname || ''}`,
    prefix: speaker.prefix || '',
    suffix: speaker.suffix || '',
    company: speaker.company || '',
    position: speaker.title || '',
    bio: speaker.bio || '',
    websites: getWebsites(speaker),
    speakerSocials: {
        linkedin: speaker.linkedinProfile || '',
        twitter: getTwitter(speaker),
    },
    speakerCommunityLike: {
        canLike: isLoggedIn,
        isLiked,
        count: speakerLikesCount && speakerLikesCount.count || 0,
        disableLikeClick: speakerLikesCount.disableLikeClick
    }
});

const getProfileImage = speaker => ({
    image: speaker.thumbnailUrl || '',
    alt: `${speaker.firstname} ${speaker.lastname} Thumbnail`
});

export const speakerSelector = createSelector(
    [
        markersSelector,
        state => state.speakerReducer.speaker,
        state => state.speakerReducer.speakerLikesCount,
        state => state.meReducer.me,
        state => state.eventReducer.event.id,
        (_, props) => props.speakerId
    ],
    (markersSelector, speaker, speakerLikesCount, user, eventId, speakerId) => {
        if (speaker === null) {
            return null;
        }
        if (speaker.id !== parseInt(speakerId)) {
            return null;
        }
        const isLoggedIn = !!user.id;

        const likeMarkerProps = getMarkerProps(eventId, user, speaker);

        const likeMarker = isLoggedIn ? markerSelector(markersSelector, likeMarkerProps) : null;

        const isLiked = !!(isLoggedIn && likeMarker);

        return {
            bio: getBio(speaker, isLoggedIn, isLiked, speakerLikesCount),
            likeMarkerProps,
            profileImage: getProfileImage(speaker),
            twitterUsername: speaker.twitterHandle || ''
        };
    }
);

export const getSessionDuration = ({ datesLabelType }, session) => {
    const { startDate, endDate, startMinute, endMinute } = session;
    if (startDate && endDate) {
        if (startDate === endDate) {
            return `( ${humanizeMinutesDuration(endMinute - startMinute)} )`;
        }
        if (endDate > startDate) {
            const minutes = getDiffInMinutes(startDate, endDate, startMinute, endMinute);
            return `( ${humanizeMinutesDuration(minutes)} )`;
        }
    } else {
        return datesLabelType === 'LABELS' ? '' : 'YET';
    }
};

export const getSessionTime = ({ timeFormat, dateFormat, datesLabelType, datesLabelMapping }, session, timeZone) => {
    const { startMinute, endMinute, startDate, endDate } = session;
    const isDatesLabelsEnabled = datesLabelType === 'LABELS';
    if (startDate && endDate) {
        const startTime = moment.utc(startDate).add(startMinute, 'minutes').format(timeFormat);
        const endTime = moment.utc(endDate).startOf('day').add(endMinute, 'minutes').format(timeFormat);
        const timezoneAbbr = moment.tz(startDate, timeZone).format('z');
        const showTimezoneAbbr = isNaN(Number(timezoneAbbr));
        const sessionStartDate = isDatesLabelsEnabled ? datesLabelMapping[`day-${startDate}`] : formatDate(startDate, dateFormat);
        let sessionTime = `${sessionStartDate} - ${startTime} - ${endTime}`;
        if (showTimezoneAbbr){
            sessionTime += ` ${timezoneAbbr}`;
        }
        return sessionTime;
    } else {
        const notSheduledCopy = isDatesLabelsEnabled ? datesLabelMapping['not-scheduled'] : 'NOT SCHEDULED, ';
        return notSheduledCopy;
    }
};

const getSessionLocationName = ({ locations }, locationId) => {
    const location = locations.find(location => location.id === locationId);
    return location ? location.name : '';
};

export const sessionsSelector = createSelector(
    [
        state => state.speakerReducer.sessions,
        state => state.speakerReducer.speaker,
        state => state.eventReducer.event,
        settingsSelector
    ],
    (sessions, speaker, event, agendaSettings) => sessions.sessions ?
        sessions.sessions.map(session => ({
            ...session,
            sessionId: session.id,
            eventId: session.eventId,
            name: session.title,
            speakerRole: session.speakers
                .find(sessionSpeaker => sessionSpeaker.speakerId === speaker.id).role || '',
            excerpt: session.descriptionHtml || '',
            location: session.locationId ? getSessionLocationName(agendaSettings, session.locationId) : ''
        })) :
        []
);

export const shouldFetchSpeakerSelector = createSelector(
    [
        shouldFetchMarkersSelector,
        shouldFetchAgendaSettingsSelector,
        (state, props) => speakerSelector(state, props)
    ],
    (shouldFetchMarkers, shouldFetchAgendaSettings, speaker) => {
        if (shouldFetchAgendaSettings || shouldFetchMarkers) {
            return true;
        }
        if (speaker === null) {
            return true;
        }
        return false;
    }
);
